import { useEffect, useCallback } from "react";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import {
  setReadings,
  setCanvases,
  setPapers,
  setSelectedReading,
  setSelectedCanvas,
  setSelectedPaper,
  setWorkspaceData,
} from "@store/slices/workspaceSlice";
import {
  selectSelectedReadingId,
  selectSelectedCanvasId,
  selectSelectedPaperId,
} from "@store/selectors";
import { selectIsDemoMode, selectSelectedOrgId } from "@store/slices/viewSlice";
import {
  getCanvases,
  getPapers,
  getReadings,
  getWorkspaceData,
  getWorkspaces,
} from "@services/api";
import {
  hydrateCurrentCanvas,
  hydrateCurrentReading,
  hydrateGradingViewForCurrentCanvas,
  hydrateGradingViewForCurrentReading,
} from "@store/thunks/workspaceThunks";
import { useNotification } from "@shared/contexts/NotificationContext";
import { useWorkspaceLoading } from "./useWorkspaceLoading";

/**
 * WorkspaceDataFetcher - A side-effect component that fetches workspace data
 * whenever the workspace ID changes. This component doesn't render anything
 * but manages data fetching for canvases, readings, and papers.
 */
const WorkspaceDataFetcher = () => {
  const dispatch = useAppDispatch();
  const { showError } = useNotification();
  const { startLoading, stopLoading } = useWorkspaceLoading();
  const { userId } = useAppSelector((state) => state.user);
  const {
    workspace_id: workspaceId,
    readings,
    canvases,
    papers,
  } = useAppSelector((state) => state.workspace);
  const selectedReadingId = useAppSelector(selectSelectedReadingId);
  const selectedCanvasId = useAppSelector(selectSelectedCanvasId);
  const selectedPaperId = useAppSelector(selectSelectedPaperId);
  const isDemoMode = useAppSelector(selectIsDemoMode);
  const selectedOrgId = useAppSelector(selectSelectedOrgId);
  const gradingViewUserId = useAppSelector(
    (state) => state.workspace.gradingView?.userId ?? null
  );

  const fetchWorkspaceData = useCallback(async () => {
    if (isDemoMode) {
      return;
    }
    if (!userId) {
      dispatch(setReadings([]));
      dispatch(setCanvases([]));
      dispatch(setPapers([]));
      return;
    }

    // No workspace in context yet — the case a freshly-signed-up user
    // lands in, since nothing else adopts the workspace created for
    // them at signup. Without this the studio sits empty for the whole
    // session (no readings, no ownership, no onboarding) and only comes
    // right after a reload, once some other flow has persisted an id.
    // Setting it here re-triggers this effect with a real workspace.
    if (!workspaceId) {
      const list = await getWorkspaces();
      if (!list.success || list.data.length === 0) {
        dispatch(setReadings([]));
        dispatch(setCanvases([]));
        dispatch(setPapers([]));
        return;
      }
      // Respect the org/personal context the user is in, matching the
      // filter WorkspaceDropdown and WorkspaceSwitcher apply.
      const inContext = list.data.filter((w) =>
        selectedOrgId === null ? !w.org_id : w.org_id === selectedOrgId
      );
      const adopted = inContext[0] ?? list.data[0];
      if (!adopted) return;
      dispatch(
        setWorkspaceData({
          workspace_id: adopted.workspace_id,
          workspace_name: adopted.workspace_name,
          shared_data: adopted.shared_data,
          user_data: adopted.user_data,
          workspace_permissions: adopted.workspace_permissions,
        })
      );
      return;
    }

    startLoading();
    try {
      console.log("Fetching workspace data for workspace:", workspaceId);

      // Fetch all data in parallel
      const [canvasesResult, readingsResult, papersResult, workspaceResult] =
        await Promise.all([
          getCanvases(workspaceId),
          getReadings(workspaceId),
          getPapers(workspaceId),
          getWorkspaceData(workspaceId),
        ]);

      // The workspace record carries workspace_permissions, which is how
      // the client derives ownership (selectIsCurrentUserWorkspaceOwner).
      // Booting straight into the studio only ever set the workspace *id*
      // (see setWorkspaceId), so every owner-gated control — Add Reading,
      // New Paper, the grading picker, first-run onboarding — behaved as
      // if the user were a contributor until they happened to open the
      // workspace switcher.
      if (workspaceResult.success) {
        const ws = workspaceResult.data;
        dispatch(
          setWorkspaceData({
            workspace_id: ws.workspace_id,
            workspace_name: ws.workspace_name,
            shared_data: ws.shared_data,
            user_data: ws.user_data,
            workspace_permissions: ws.workspace_permissions,
          })
        );
      } else {
        console.error(
          "Error fetching workspace record:",
          workspaceResult.error.message
        );
      }

      console.debug("readings result", readingsResult);

      // Update Redux store with fetched data
      const errors: string[] = [];

      if (!canvasesResult.success) {
        console.error("Error fetching canvases:", canvasesResult.error.message);
        errors.push("canvases");
      } else {
        dispatch(setCanvases(Array.isArray(canvasesResult.data) ? canvasesResult.data : []));
      }

      if (!readingsResult.success) {
        console.error("Error fetching readings:", readingsResult.error.message);
        errors.push("readings");
      } else {
        dispatch(setReadings(Array.isArray(readingsResult.data) ? readingsResult.data : []));
      }

      if (!papersResult.success) {
        console.error("Error fetching papers:", papersResult.error.message);
        errors.push("papers");
      } else {
        dispatch(setPapers(Array.isArray(papersResult.data) ? papersResult.data : []));
      }

      if (errors.length > 0) {
        showError(`Failed to load ${errors.join(", ")}`);
      }
    } catch (error) {
      console.error("Error fetching workspace data:", error);
      showError("Failed to load workspace data");
      // Clear data on error
      dispatch(setReadings([]));
      dispatch(setCanvases([]));
      dispatch(setPapers([]));
    } finally {
      stopLoading();
    }
  }, [dispatch, userId, workspaceId, selectedOrgId, isDemoMode, showError, startLoading, stopLoading]);

  useEffect(() => {
    fetchWorkspaceData();
  }, [fetchWorkspaceData]);

  // Auto-select first reading if none selected OR selected reading doesn't exist
  useEffect(() => {
    const firstReading = readings[0];
    const selectedReadingExists = selectedReadingId && readings.some(r => r.reading_id === selectedReadingId);

    if (firstReading && !selectedReadingExists) {
      console.log("Auto-selecting first reading:", firstReading.reading_id);
      dispatch(setSelectedReading(firstReading.reading_id));
    } else if (!firstReading && selectedReadingId) {
      // Clear stale selection if no readings available
      dispatch(setSelectedReading(null));
    }
  }, [readings, selectedReadingId, dispatch]);

  useEffect(() => {
    if (isDemoMode) return;
    if (selectedReadingId) {
      dispatch(hydrateCurrentReading(selectedReadingId));
    }
  }, [selectedReadingId, isDemoMode, dispatch]);

  useEffect(() => {
    if (isDemoMode) return;
    if (selectedReadingId && gradingViewUserId) {
      dispatch(hydrateGradingViewForCurrentReading());
    }
  }, [selectedReadingId, gradingViewUserId, isDemoMode, dispatch]);

  // Auto-select first canvas if none selected OR selected canvas doesn't exist
  useEffect(() => {
    const firstCanvas = canvases[0];
    const selectedCanvasExists = selectedCanvasId && canvases.some(c => c.canvas_id === selectedCanvasId);

    if (firstCanvas && !selectedCanvasExists) {
      console.log("Auto-selecting first canvas:", firstCanvas.canvas_id);
      dispatch(setSelectedCanvas(firstCanvas.canvas_id));
    } else if (!firstCanvas && selectedCanvasId) {
      // Clear stale selection if no canvases available
      dispatch(setSelectedCanvas(null));
    }
  }, [canvases, selectedCanvasId, dispatch]);

  useEffect(() => {
    if (isDemoMode) return;
    if (selectedCanvasId) {
      dispatch(hydrateCurrentCanvas(selectedCanvasId));
    }
  }, [selectedCanvasId, isDemoMode, dispatch]);

  useEffect(() => {
    if (isDemoMode) return;
    if (selectedCanvasId && gradingViewUserId) {
      dispatch(hydrateGradingViewForCurrentCanvas());
    }
  }, [selectedCanvasId, gradingViewUserId, isDemoMode, dispatch]);

  // Auto-select first paper if none selected OR selected paper doesn't exist
  useEffect(() => {
    const firstPaper = papers[0];
    const selectedPaperExists = selectedPaperId && papers.some(p => p.paper_id === selectedPaperId);

    if (firstPaper && !selectedPaperExists) {
      console.log("Auto-selecting first paper:", firstPaper.paper_id);
      dispatch(setSelectedPaper(firstPaper.paper_id));
    } else if (!firstPaper && selectedPaperId) {
      // Clear stale selection if no papers available
      dispatch(setSelectedPaper(null));
    }
  }, [papers, selectedPaperId, dispatch]);

  // This component doesn't render anything
  return null;
};

export default WorkspaceDataFetcher;
